import React, { useState, useEffect, FC } from "react";
import {
  Home,
  ClipboardList,
  Tags,
  BookOpenCheck,
  ChevronLeft,
  ChevronRight,
  LogOut,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
type SideBarTypes = {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
};

const Sidebar: FC<SideBarTypes> = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navItems = [
    {
      name: "My Exams",
      icon: ClipboardList,
      active: `/dashboard/my-exams` === location.pathname,
      location: `/dashboard/my-exams`,
    },
    {
      name: "Exam Results",
      icon: Tags,
      active: `/dashboard/exams-result` === location.pathname,
      location: `/dashboard/exams-result`,
    },
    {
      name: "Raise Objection",
      icon: BookOpenCheck,
      active: `/dashboard/raise-objection` === location.pathname,
      location: `/dashboard/raise-objection`,
    },
  ];

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  return (
    <aside
      className={`bg-neutral-50 border-r border-gray-200 fixed h-[93.2vh] z-20 shadow-sm transition-all duration-300 ease-in-out flex flex-col ${
        collapsed ? "w-16" : "w-56"
      } ${isMobile && collapsed ? "-translate-x-full" : "translate-x-0"}`}
    >
      {/* Collapse Button */}
      <button
        onClick={toggleSidebar}
        className="absolute -right-6 top-[18px] z-10 flex items-center justify-center w-6 h-10 bg-neutral-50 rounded-r-lg border border-l-0 border-gray-200 hover:bg-gray-50 transition-colors duration-200 shadow-sm"
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronLeft className="h-4 w-4 text-gray-500" />
        )}
      </button>

      {/* Navigation Items */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <ul className="space-y-1">
          {navItems.map((item, index) => (
            <li key={index} className="min-w-0">
              <button
                onClick={() => {
                  navigate(item.location);
                  if (isMobile) setCollapsed(true);
                }}
                className={`flex items-center w-full p-2 rounded-lg transition-all duration-200 ${
                  item.active
                    ? "bg-blue-100 text-blue-600 font-medium"
                    : "text-gray-600 hover:bg-gray-100"
                } ${collapsed ? "justify-center" : "justify-start"}`}
              >
                <div
                  className={`flex-shrink-0 p-1.5 rounded-lg ${
                    item.active ? "bg-blue-200" : "bg-gray-200"
                  }`}
                >
                  <item.icon
                    className={`h-4 w-4 ${
                      item.active ? "text-blue-600" : "text-gray-500"
                    }`}
                  />
                </div>
                {!collapsed && (
                  <span className="ml-3 text-sm font-semibold  truncate min-w-0">
                    {item.name}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Logout Section - Fixed at bottom */}
      <div className="p-2 border-t border-gray-200">
        <button
          onClick={() => {
            // Handle logout logic here
            window.location.reload();
          }}
          className={`group flex items-center w-full p-2 rounded-lg transition-colors duration-200 ${
            collapsed ? "justify-center" : "justify-between"
          }`}
        >
          <div className="flex items-center">
            <div className="p-1.5 rounded-lg bg-gray-100 group-hover:bg-red-100 transition-colors duration-200">
              <LogOut className="h-4 w-4 text-gray-500 group-hover:text-red-500 transition-colors duration-200" />
            </div>
            {!collapsed && (
              <span className="ml-3 text-sm font-semibold text-gray-600 group-hover:text-red-600 transition-colors duration-200">
                Log out
              </span>
            )}
          </div>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
